import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";

// Config
import { snackbarTypes } from "../../config/snackbar";
import { urls } from "../../config/routes";
import { userTypes } from "../../config/values";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Actions
import { getCaseDetail } from "../../state/caseDetail/actions";
import { getInvitations } from "../../state/invitations/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useShowSteps from "../../hooks/useShowSteps";
import useCases from "../../hooks/useCases";

// Context
import CaseDetailStatusContext from "../../context/CaseDetailStatusContext";

// Components
import CaseContributors from "../CaseContributors";
import CaseDetail from "../CaseDetail";
import CaseSummary from "../CaseSummary";
import GoBackLink from "../GoBackLink";
import LoadingSection from "../LoadingSection";
import MainSection from "../MainSection";
import VictimSteps from "../VictimSteps";

const useStyles = makeStyles(theme => ({
  root: {
    ...styles.mixins.mainSection,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    paddingBottom: theme.spacing(5)
  },
  section: {
    ...styles.mixins.sectionContainer,
    // GoBackLink normally provides the top gap below the header - replace it
    // when hideSidebar skips GoBackLink entirely.
    paddingTop: ({ hideSidebar }) => (hideSidebar ? theme.spacing(2.5) : 0)
  },
  goBack: {
    padding: theme.spacing(2.5, 0, 1.5),
    width: "100%"
  },
  goBackLink: {
    textTransform: "uppercase"
  },
  chevron: {
    marginRight: theme.spacing(2)
  },
  goBackText: {
    color: colors.cloudBurst
  },
  leftContent: {
    paddingRight: theme.spacing(2)
  },
  rightContent: {
    paddingLeft: theme.spacing(2)
  }
}));

const SectionWrapper = ({ children, hideSidebar, isNew, userCapacity }) => {
  const { caseId } = useParams();
  const { trackException } = useAppInsights();
  const classes = useStyles({ hideSidebar });
  const dispatch = useDispatch();
  const history = useHistory();

  const { data, isLoading, error } = useSelector(s => s.caseDetail);
  const invitations = useSelector(s => s.invitations);
  const showCaseSteps = useShowSteps(caseId);
  const cases = useCases();
  const [isLoadingStale, setIsLoadingStale] = useState(false);

  useEffect(() => {
    if (!invitations.data && !invitations.isLoading && !invitations.error) {
      dispatch(
        getInvitations(() => trackException("Failed to get invitations"))
      );
    }
  }, [
    dispatch,
    invitations.data,
    invitations.error,
    invitations.isLoading,
    trackException
  ]);

  useEffect(() => {
    if (error) {
      history.push(urls.cases);
    } else if (!isNew) {
      if (!caseId || caseId === "undefined") {
        history.push(urls.cases);
        return;
      }

      // Each case sub-page mounts its own SectionWrapper, so this effect
      // re-runs on every within-case navigation, not just a remount at the
      // same route - fetching unconditionally here is what keeps sidebar
      // content (e.g. the injuries/physical damage accordions) from going
      // stale as the user moves between sub-pages without ever returning to
      // the case detail root.
      const isReady = invitations.data && !isLoading;

      if (isReady) {
        const onFail = callback => {
          if (invitations.data.findIndex(i => i.id === caseId) !== -1) {
            dispatch(
              toggleSnack({
                type: snackbarTypes.ERROR,
                text: "caseMenu.pleaseAcceptInvitation"
              })
            );
          } else {
            trackException("Failed to get case details");
            if (callback) callback();
          }
          history.push(urls.cases);
        };

        // isUpdate keeps `data`/`isLoading` untouched until the response
        // arrives, so a same-case refresh doesn't flash the loading state -
        // but a genuine case switch has nothing worth keeping, so it clears
        // `data` immediately instead of leaving the previous case in the
        // store until the new one arrives.
        const isSameCase = !!data && data.id === caseId;
        setIsLoadingStale(true);
        dispatch(
          getCaseDetail({
            avoidError: true,
            caseId,
            onFail,
            isUpdate: isSameCase,
            onFinish: () => setIsLoadingStale(false)
          })
        );
      }
    }
    // `data`/`isLoading` excluded on purpose - this effect's own dispatch
    // changes both, which would otherwise retrigger it in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, dispatch, error, history, invitations.data, isNew, trackException]);

  const getPendingStatements = () => {
    const selectedCase = cases.data?.find(c => c.id === data.id);
    return !!selectedCase?.hasPendingStatements;
  };

  const content = isNew ? {} : data;
  const isNewIntel = isNew && userCapacity === "intelligence";
  const isWitness = !!data && data.userCapacity === userTypes.Witness;

  if (data && data.id === caseId && data.isRestrictedForCurrentUser) {
    history.push(urls.cases);
    return null;
  }

  const isStaleForThisCase = !data || data.id !== caseId;
  const casesListUnavailable = cases.isLoading && !cases.data;

  return !isNew &&
    (isStaleForThisCase || casesListUnavailable || !content) ? (
    <MainSection>
      <LoadingSection />
    </MainSection>
  ) : (
    <CaseDetailStatusContext.Provider value={{ isLoadingStale }}>
      <MainSection>
        <Grid container className={classes.section}>
          {!hideSidebar && <GoBackLink />}
          {!hideSidebar && (
            <Grid item xs={4} className={classes.leftContent}>
              <CaseDetail
                data={content}
                isNew={isNew}
                isNewIntel={isNewIntel}
                isWitness={isWitness}
                showSteps={showCaseSteps}
              />
              {!isNew && !showCaseSteps && (
                <>
                  <CaseSummary
                    caseId={caseId}
                    isDraft={data.isDraft}
                    isWitness={isWitness}
                    isIntel={data.isIntel}
                    hasPendingStatements={getPendingStatements()}
                  />
                  {data && (
                    <CaseContributors
                      capacity={data.userCapacity}
                      caseId={caseId}
                      creatorId={data.creatorId}
                      isDraft={data.isDraft}
                      isWitness={isWitness}
                    />
                  )}
                </>
              )}
            </Grid>
          )}
          <Grid
            item
            xs={hideSidebar ? 12 : 8}
            className={hideSidebar ? undefined : classes.rightContent}
          >
            {showCaseSteps ? <VictimSteps caseId={caseId} /> : children}
          </Grid>
        </Grid>
      </MainSection>
    </CaseDetailStatusContext.Provider>
  );
};

SectionWrapper.propTypes = {
  children: PropTypes.node,
  hideSidebar: PropTypes.bool,
  isNew: PropTypes.bool,
  userCapacity: PropTypes.string
};

SectionWrapper.defaultProps = {
  children: null,
  hideSidebar: false,
  isNew: false,
  userCapacity: null
};

export default SectionWrapper;
